import { cweaCartsFetch } from '@libs/api-proxy-client';
import {
    AddBillingAddressRequestBody,
    AddDonationRequestBody,
    AddItemRequestBody,
    AddPaymentMethodRequestBody,
    AddPrescriptionBody,
    AddShippingAddressRequestBody,
    ApplyPromoCodeRequestBody,
    BaseError,
    CartCreateBody,
    CartUI,
    CartValidationErrorResponse,
    CartValidationResponse,
    DeliveryOptions,
    DeliveryOptionsPost,
    EstimatedTreatsPoints,
    GuestEmailRequestBody,
    InventoryResponseError,
    LineItemResponseError,
    Order,
    OrderRedirect,
    PickupInfoRequestBody,
    PlaceOrderRequestBody,
    SetShippingMethodRequestBody,
    ShippingAddressValidationResult,
    ShippingMethods,
    TreatPointRequestBody,
    UpdateItemFulfillmentMethodRequestBody,
    UpdateItemQtyRequestBody,
    UpdateItemStoreRequestBody,
    UpdateSddDropoffAddressRequestBody,
    UpdateShippingAddressRequestBody,
} from '@libs/common-node';
import type { Nullable } from '@libs/core';
import { AUTH_COOKIES, AUTH_HEADERS, HttpMethod, HttpStatus } from '@libs/core';

import { CartValidationError, isCartValidationErrorResponse } from '../providers/utils/error';
import { getNextApiRoute } from './api';
import { getHeaders } from './fetchCart';
import { getCookieValue } from './getCookieValue';
import { CartValidationErrorKey } from './notificationMaps';
import { trackInventoryImpact } from './sentryTracker';
import { typedFetch } from './typedFetch';

const CARTS_BASE = 'carts';

const cartSubpath = (cartId: string, ...segments: string[]): string =>
    `${CARTS_BASE}/${[cartId, ...segments].join('/')}`;

const withVersion = (path: string, version: number): string => `${path}?version=${version}`;

const withQueryString = (path: string, query: string): string => `${path}?${query}`;

export const getEstimatedTreatsPointsForCart = async ({
    cartId,
    loyaltyId,
}: {
    cartId: string;
    loyaltyId?: number;
}): Promise<EstimatedTreatsPoints | null> => {
    const url = getNextApiRoute(`loyalty/v2/points/estimate/${cartId}`);
    const headers: HeadersInit = {};

    if (loyaltyId) {
        headers[AUTH_HEADERS.X_PETM_LOYALTY_NUMBER] = `${loyaltyId}`;
    }

    return await typedFetch<EstimatedTreatsPoints>(url, { headers });
};

export const createCart = async ({ body }: { body: CartCreateBody }) =>
    cweaCartsFetch<CartUI | null, CartCreateBody>({
        method: HttpMethod.POST,
        pathname: `${CARTS_BASE}/items`,
        body,
    });

export const getCartById = async ({ cartId, validate }: { cartId: string; validate?: boolean }) =>
    cweaCartsFetch<CartUI | null>({
        method: HttpMethod.GET,
        pathname: `${CARTS_BASE}/${cartId}${validate ? '?validate=true' : ''}`,
        additionalSuccessCodes: [303, 400, 404],
    });

export const addItem = async ({
    cartId,
    version,
    body,
}: {
    cartId?: string;
    version?: number;
    body: AddItemRequestBody;
}) => {
    const itemsPath = `${CARTS_BASE}/items`;
    const path = cartId && version ? `${itemsPath}?id=${cartId}&version=${version}` : itemsPath;
    return cweaCartsFetch<CartUI | null, AddItemRequestBody>({
        method: HttpMethod.POST,
        pathname: path,
        body,
    });
};

export const updateItemQty = async ({
    cartId,
    itemId,
    version,
    body,
}: {
    cartId: string;
    itemId: string;
    version: number;
    body: UpdateItemQtyRequestBody;
}) =>
    cweaCartsFetch<CartUI | null, UpdateItemQtyRequestBody>({
        method: HttpMethod.PUT,
        pathname: withVersion(cartSubpath(cartId, 'items', itemId), version),
        body,
    });

export const updateSubscriptionStatus = async ({ cartId, version }: { cartId: string; version: number }) =>
    cweaCartsFetch<CartUI | null>({
        method: HttpMethod.POST,
        pathname: withVersion(cartSubpath(cartId, 'update-subscriptions-status'), version),
    });

export const updateItemFulfillmentMethod = async ({
    cartId,
    itemId,
    version,
    body,
}: {
    cartId: string;
    itemId: string;
    version: number;
    body: UpdateItemFulfillmentMethodRequestBody;
}) =>
    cweaCartsFetch<CartUI | null, UpdateItemFulfillmentMethodRequestBody>({
        method: HttpMethod.PUT,
        pathname: withVersion(cartSubpath(cartId, 'items', itemId, 'fulfillment-method'), version),
        body,
    });

export const addBillingAddress = async ({
    cartId,
    version,
    body,
}: {
    cartId: string;
    version: number;
    body: AddBillingAddressRequestBody;
}) =>
    cweaCartsFetch<CartUI | null, AddBillingAddressRequestBody>({
        method: HttpMethod.POST,
        pathname: withVersion(cartSubpath(cartId, 'billing-addresses'), version),
        body,
    });

export const addShippingAddress = async ({
    cartId,
    version,
    body,
}: {
    cartId: string;
    version: number;
    body: AddShippingAddressRequestBody;
}) =>
    await cweaCartsFetch<CartUI | null, AddShippingAddressRequestBody>({
        method: HttpMethod.POST,
        pathname: withVersion(cartSubpath(cartId, 'shipping-addresses'), version),
        body,
    });

export const updateShippingAddress = async ({
    cartId,
    version,
    body,
}: {
    cartId: string;
    version: number;
    body: UpdateShippingAddressRequestBody;
}) =>
    await cweaCartsFetch<CartUI | null, UpdateShippingAddressRequestBody>({
        method: HttpMethod.PUT,
        pathname: withVersion(cartSubpath(cartId, 'shipping-addresses'), version),
        body,
    });

export const updateSddDropoffAddress = async ({
    cartId,
    version,
    body,
}: {
    cartId: string;
    version: number;
    body: UpdateSddDropoffAddressRequestBody;
}) =>
    await cweaCartsFetch<CartUI | null, UpdateSddDropoffAddressRequestBody>({
        method: HttpMethod.PUT,
        pathname: withVersion(cartSubpath(cartId, 'sdd-dropoff-address'), version),
        body,
    });

export const selectExistingShippingAddress = async ({
    cartId,
    version,
    body,
}: {
    cartId: string;
    version: number;
    body: { addressId: number };
}) =>
    await cweaCartsFetch<CartUI | null, { addressId: number }>({
        method: HttpMethod.PUT,
        pathname: withVersion(cartSubpath(cartId, 'select-existing-address'), version),
        body,
    });

export const addPayment = async ({
    cartId,
    version,
    body,
}: {
    cartId: string;
    version: number;
    body: AddPaymentMethodRequestBody;
}) => {
    if (body.giftCardNumber) {
        // Check if checkbox mode is active, then read from the appropriate cookie
        const isCheckbox = getCookieValue(AUTH_COOKIES.RECAPTCHA_IS_CHECKBOX) === 'true';
        const token = isCheckbox
            ? getCookieValue(AUTH_COOKIES.RECAPTCHA_TOKEN_CHECKBOX)
            : getCookieValue(AUTH_COOKIES.RECAPTCHA_TOKEN);

        return cweaCartsFetch<CartUI | null, AddPaymentMethodRequestBody>({
            method: HttpMethod.POST,
            pathname: withVersion(cartSubpath(cartId, 'payments'), version),
            body: { ...body, recaptchaToken: token ?? '' },
            additionalSuccessCodes: [303, 400, 500],
        });
    }
    return cweaCartsFetch<CartUI | null, AddPaymentMethodRequestBody>({
        method: HttpMethod.POST,
        pathname: withVersion(cartSubpath(cartId, 'payments'), version),
        body,
        additionalSuccessCodes: [303, 400, 500],
    });
};

export const removePayment = async ({
    cartId,
    version,
    body,
}: {
    cartId: string;
    version: number;
    body: { methodId: string };
}) =>
    cweaCartsFetch<CartUI | null>({
        method: HttpMethod.DELETE,
        pathname: withVersion(cartSubpath(cartId, 'payments', body.methodId), version),
    });

export const removeItem = async ({ cartId, itemId, version }: { cartId: string; itemId: string; version: number }) =>
    cweaCartsFetch<CartUI | null>({
        method: HttpMethod.DELETE,
        pathname: withVersion(cartSubpath(cartId, 'items', itemId), version),
    });

export const getShippingMethods = async ({ cartId }: { cartId: string }) =>
    cweaCartsFetch<ShippingMethods[] | null>({
        method: HttpMethod.GET,
        pathname: cartSubpath(cartId, 'shipping-methods'),
    });

export const getDeliveryOptions = async ({ cartId }: { cartId: string }) =>
    cweaCartsFetch<DeliveryOptions | null>({
        method: HttpMethod.GET,
        pathname: cartSubpath(cartId, 'delivery-options'),
    });

export const setDeliveryOptions = async ({
    cartId,
    version,
    body,
}: {
    cartId: string;
    version: number;
    body: DeliveryOptionsPost;
}) =>
    cweaCartsFetch<CartUI | null, DeliveryOptionsPost>({
        method: HttpMethod.POST,
        pathname: withVersion(cartSubpath(cartId, 'delivery-options'), version),
        body,
    });

export const validateShippingAddresses = async (cartId: string) =>
    cweaCartsFetch<ShippingAddressValidationResult>({
        method: HttpMethod.GET,
        pathname: cartSubpath(cartId, 'addresses'),
    });

export const updateItemStore = async ({
    cartId,
    itemId,
    body,
}: {
    cartId: string;
    itemId: string;
    body: UpdateItemStoreRequestBody;
}) =>
    cweaCartsFetch<CartUI | null, UpdateItemStoreRequestBody>({
        method: HttpMethod.PUT,
        pathname: cartSubpath(cartId, 'items', itemId, 'store'),
        body,
    });

export const addDonation = async ({
    cartId,
    version,
    body,
}: {
    cartId: string;
    version: number;
    body: AddDonationRequestBody;
}) =>
    cweaCartsFetch<CartUI | null, AddDonationRequestBody>({
        method: HttpMethod.POST,
        pathname: withVersion(cartSubpath(cartId, 'donations'), version),
        body,
    });

export const removeDonation = async ({ cartId, version }: { cartId: string; version: number }) =>
    cweaCartsFetch<CartUI | null>({
        method: HttpMethod.DELETE,
        pathname: withVersion(cartSubpath(cartId, 'donations'), version),
    });

export const addDiscountCode = async ({
    cartId,
    version,
    body,
}: {
    cartId: string;
    version: number;
    body: ApplyPromoCodeRequestBody;
}) =>
    cweaCartsFetch<CartUI | null, ApplyPromoCodeRequestBody>({
        method: HttpMethod.POST,
        pathname: withVersion(cartSubpath(cartId, 'discount-codes'), version),
        body,
    });

export const removeDiscountCode = async ({
    cartId,
    version,
    body,
}: {
    cartId: string;
    version: number;
    body: { codeId: string };
}) =>
    cweaCartsFetch<CartUI | null>({
        method: HttpMethod.DELETE,
        pathname: withVersion(cartSubpath(cartId, 'discount-codes', body.codeId), version),
    });

export const addGuestEmail = async ({
    cartId,
    version,
    body,
}: {
    cartId: string;
    version: number;
    body: GuestEmailRequestBody;
}) =>
    cweaCartsFetch<CartUI | null, GuestEmailRequestBody>({
        method: HttpMethod.POST,
        pathname: withVersion(cartSubpath(cartId, 'email-address'), version),
        body,
    });

export const addPickupInfo = async ({
    cartId,
    body,
    version,
}: {
    cartId: string;
    body: PickupInfoRequestBody;
    version: number;
}) =>
    cweaCartsFetch<CartUI | null, PickupInfoRequestBody>({
        method: HttpMethod.POST,
        pathname: withVersion(cartSubpath(cartId, 'pickup'), version),
        body,
    });

export const addTreatPoints = async ({
    cartId,
    body,
    version,
}: {
    cartId: string;
    body: TreatPointRequestBody & {
        loyaltyId: Nullable<number>;
    };
    version: number;
}) => {
    const { amount, loyaltyId } = body;
    return cweaCartsFetch<CartUI | null, { amount: number }>({
        method: HttpMethod.POST,
        pathname: withVersion(cartSubpath(cartId, 'treat-points'), version),
        body: { amount },
        headers: {
            [AUTH_HEADERS.X_PETM_LOYALTY_NUMBER]: loyaltyId ? `${loyaltyId}` : '',
        },
    });
};

export const removeTreatPoints = async ({ cartId, version }: { cartId: string; version: number }) =>
    cweaCartsFetch<CartUI | null>({
        method: HttpMethod.DELETE,
        pathname: withVersion(cartSubpath(cartId, 'treat-points'), version),
    });

export const placeOrder = async ({
    cartId,
    body,
    version,
}: {
    cartId: string;
    body: PlaceOrderRequestBody;
    version: number;
}) =>
    cweaCartsFetch<Order | OrderRedirect | null, PlaceOrderRequestBody>({
        method: HttpMethod.POST,
        pathname: withVersion(`${CARTS_BASE}/orderV2/${cartId}`, version),
        body,
        headers: getHeaders(),
        additionalSuccessCodes: [422, 503, 400, HttpStatus.CONFLICT, HttpStatus.NOT_FOUND, HttpStatus.GONE],
    });

export const setShippingMethods = async ({
    cartId,
    version,
    body,
}: {
    cartId: string;
    version: number;
    body: SetShippingMethodRequestBody;
}) =>
    cweaCartsFetch<CartUI | null, SetShippingMethodRequestBody>({
        method: HttpMethod.POST,
        pathname: withVersion(cartSubpath(cartId, 'shipping-methods'), version),
        body,
    });

export const addLegacyPrescription = async ({
    cartId,
    itemId,
    body,
}: {
    cartId: string;
    itemId: string;
    body: AddPrescriptionBody;
}) =>
    cweaCartsFetch<CartUI | null, AddPrescriptionBody>({
        method: HttpMethod.POST,
        pathname: cartSubpath(cartId, 'items', itemId, 'prescription'),
        body,
    });

export const addPrescription = async ({
    cartId,
    itemId,
    body,
}: {
    cartId: string;
    itemId: string;
    body: AddPrescriptionBody;
}) =>
    cweaCartsFetch<CartUI | null, AddPrescriptionBody>({
        method: HttpMethod.POST,
        pathname: cartSubpath(cartId, 'items', itemId, 'prescription', 'v2'),
        body,
    });

const buildValidationParams = (
    useRealtimeInventory: boolean,
    silentUpdate: boolean,
    forceTaxRefresh: boolean
): URLSearchParams => {
    const params = new URLSearchParams();

    if (useRealtimeInventory) {
        params.set('useRealtimeInventory', 'true');
    }

    if (silentUpdate) {
        params.set('silentUpdate', 'true');
    }

    if (forceTaxRefresh) {
        params.set('forceTaxRefresh', 'true');
    }

    return params;
};

const getStockRelatedErrors = (response: CartValidationErrorResponse) => {
    const cartErrors = response.errors?.cartErrors || [];
    const inventoryErrors = response.inventory_errors || [];
    const lineItemErrors = response.errors?.lineErrors || [];

    return [
        ...cartErrors,
        ...inventoryErrors,
        ...lineItemErrors.flatMap((le: LineItemResponseError) => le.lineItemErrors || []),
    ].filter(
        (error: BaseError | InventoryResponseError) =>
            error.errorCode?.includes(CartValidationErrorKey.OUT_OF_STOCK) ||
            error.errorCode?.includes(CartValidationErrorKey.INSUFFICIENT_STOCK)
    );
};

const trackStockErrors = (
    cartId: string,
    response: CartValidationErrorResponse,
    stockRelatedErrors: (BaseError | InventoryResponseError)[],
    useRealtimeInventory: boolean
) => {
    if (stockRelatedErrors.length > 0) {
        trackInventoryImpact(
            new Error(response.title),
            { cartId },
            {
                itemCount: stockRelatedErrors.length,
                validation_type: useRealtimeInventory ? 'realtime' : 'standard',
                impact_rating: stockRelatedErrors.length > 2 ? 'high' : 'medium',
            }
        );
    }
};

const handleValidationError = (response: CartValidationErrorResponse, throwOnError: boolean) => {
    // Check for specific error codes that require throwing a CartValidationError
    const errorCode = response.errors?.cartErrors?.find(
        (cartError: BaseError) =>
            cartError.errorCode === CartValidationErrorKey.MIXED_CART_BANNER ||
            cartError.errorCode === CartValidationErrorKey.MIXED_RX_COLD_ITEM ||
            cartError.errorCode === CartValidationErrorKey.DISCONTINUED_PRODUCT ||
            cartError.errorCode === CartValidationErrorKey.AUTOSHIP_NOT_SUPPORTED
    );

    const lineErrors = response.errors?.lineErrors
        ?.flatMap((le: LineItemResponseError) => le.lineItemErrors)
        ?.filter(
            (error: BaseError | InventoryResponseError) =>
                error.errorCode?.includes(CartValidationErrorKey.OUT_OF_STOCK) ||
                error.errorCode?.includes(CartValidationErrorKey.INSUFFICIENT_STOCK) ||
                error.errorCode?.includes(CartValidationErrorKey.DISCONTINUED_PRODUCT) ||
                error.errorCode?.includes(CartValidationErrorKey.AUTOSHIP_NOT_SUPPORTED)
        );

    if (throwOnError || errorCode || lineErrors) {
        throw new CartValidationError(response.title, response);
    } else {
        throw new Error('Out of stock');
    }
};

export const validateCart = async ({
    cartId,
    useRealtimeInventory = false,
    silentUpdate = false,
    throwOnError = true,
    forceTaxRefresh = false,
}: {
    cartId: string;
    useRealtimeInventory?: boolean;
    silentUpdate?: boolean;
    throwOnError?: boolean;
    forceTaxRefresh?: boolean;
}) => {
    const params = buildValidationParams(useRealtimeInventory, silentUpdate, forceTaxRefresh);

    const response = await cweaCartsFetch<CartValidationResponse | CartUI>({
        method: HttpMethod.PUT,
        pathname: withQueryString(cartSubpath(cartId, 'validate'), params.toString()),
        additionalSuccessCodes: [303, 422],
    });

    if (isCartValidationErrorResponse(response)) {
        const stockRelatedErrors = getStockRelatedErrors(response);
        trackStockErrors(cartId, response, stockRelatedErrors, useRealtimeInventory);
        handleValidationError(response, throwOnError);
    }

    return response;
};
